<script setup lang="ts">
import {
  type ComponentPublicInstance,
  computed,
  onBeforeUnmount,
  ref,
  watch,
} from 'vue';
import { trackPageView } from '@/api/tracking';
import { DgrLoading } from '@stockmarkteam/donguri-ui';
import { provideInsightPageName } from '@/components/home/insights/use-insight-tracking';
import SavedInsightDetailBody from '@/components/insights/saved/saved-insight-detail-body.vue';
import SavedInsightDetailHeader from '@/components/insights/saved/saved-insight-detail-header.vue';
import { resolveSavedInsightsTabView } from '@/components/insights/saved/saved-insight-display';
import SavedInsightsEmpty from '@/components/insights/saved/saved-insights-empty.vue';
import SavedInsightsLoadFailure from '@/components/insights/saved/saved-insights-load-failure.vue';
import { useSavedInsightDetailsPagination } from '@/components/insights/saved/use-saved-insights-pagination';
import { useCurrentPathForTracking } from '@/utils/composables/useCurrentPathForTracking';

const SENTINEL_OFFSET_FROM_END = 3;

provideInsightPageName('insight_saved');

const { getCurrentPathForTracking } = useCurrentPathForTracking();
void trackPageView({
  pageName: 'insight_saved',
  pageUrl: getCurrentPathForTracking(),
});

const {
  hints,
  state,
  isLoadingNextPage,
  nextPageError,
  loadNextPage,
  retryNextPage,
  reset,
  removeHint,
} = useSavedInsightDetailsPagination();

// 今日タブで保存した分を反映するため、タブを開くたびに 1 ページ目から取り直す。
reset();

const view = computed(() =>
  resolveSavedInsightsTabView(state.value, hints.value.length),
);
const sentinelIndex = computed(() =>
  Math.max(hints.value.length - SENTINEL_OFFSET_FROM_END, 0),
);

const sentinel = ref<HTMLElement>();
const setSentinel = (el: Element | ComponentPublicInstance | null) => {
  sentinel.value = el instanceof HTMLElement ? el : undefined;
};

const observer = new IntersectionObserver(entries => {
  if (entries.some(entry => entry.isIntersecting)) void loadNextPage();
});

watch(sentinel, (current, previous) => {
  if (previous) observer.unobserve(previous);
  if (current) observer.observe(current);
});

onBeforeUnmount(() => observer.disconnect());
</script>

<template>
  <div class="insights-saved">
    <div v-if="view === 'loading'" class="loading">
      <DgrLoading />
    </div>
    <SavedInsightsEmpty v-else-if="view === 'empty'" />
    <SavedInsightsLoadFailure v-else-if="view === 'failed'" @reload="reset" />
    <div v-else class="saved-cards">
      <div v-for="(detail, index) in hints" :key="detail.hint.uuid">
        <div
          v-if="index === sentinelIndex"
          :ref="setSentinel"
          class="sentinel"
        ></div>
        <div class="saved-card">
          <SavedInsightDetailHeader
            :detail="detail"
            @unsaved="removeHint(detail.hint.uuid)"
          />
          <SavedInsightDetailBody :detail="detail" />
        </div>
      </div>
      <div v-if="nextPageError" class="next-page-error">
        <div class="c-text-regular--s">読み込みに失敗しました。</div>
        <button
          type="button"
          class="c-btn c-btn--auto c-btn--small c-outlineBtn"
          @click="retryNextPage"
        >
          再度読み込む
        </button>
      </div>
      <div
        v-else-if="isLoadingNextPage"
        class="next-page-indicator c-text-regular--s"
      >
        読み込み中...
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.insights-saved {
  padding-bottom: 24px;
}

.loading {
  display: flex;
  justify-content: center;
  padding: 24px;
}

.saved-cards {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.saved-card {
  overflow: hidden;
  border: 1px solid $color-gray400;
  border-radius: 4px;
  background-color: $color-white;

  :deep(.saved-insight-detail-body) {
    width: 100%;
    margin: 0;
  }
}

.sentinel {
  height: 0;
}

.next-page-indicator {
  padding: 8px;
  text-align: center;
  color: $color-gray800;
}

.next-page-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 8px;
  color: $color-gray800;
}
</style>
